import { Link } from "react-router-dom";
import { Trash2 } from "lucide-react";
import GlossyIcon from "@/components/GlossyIcon";
import { useWishlist } from "@/contexts/WishlistContext";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { getFirstImage, getEffectivePrice } from "@/hooks/useProducts";
import { useCart } from "@/contexts/CartContext";
import { toast } from "sonner";
import Ticker from "@/components/Ticker";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";

const WishlistPage = () => {
  const { wishlistIds, toggleWishlist } = useWishlist();
  const { addItem } = useCart();

  const { data: products = [], isLoading } = useQuery({
    queryKey: ["wishlist-products", [...wishlistIds]],
    queryFn: async () => {
      if (wishlistIds.size === 0) return [];
      const { data, error } = await supabase
        .from("products")
        .select("*")
        .in("id", [...wishlistIds]);
      if (error) throw error;
      return data;
    },
    enabled: wishlistIds.size > 0,
  });

  return (
    <div className="min-h-screen flex flex-col">
      <SEO title="My Wishlist" description="Your saved Plymhair wigs, hair extensions, braids and ponytails — ready when you are." url="/wishlist" />
      <Ticker />
      <Navbar />

      <div className="container py-8 flex-1">
        <h1 className="font-display text-2xl md:text-3xl font-bold mb-2">
          My Wishlist
        </h1>
        <p className="text-sm text-muted-foreground font-body mb-8">
          {wishlistIds.size} saved items
        </p>

        {isLoading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-square bg-muted rounded mb-3" />
                <div className="h-3 bg-muted rounded w-2/3 mb-2" />
                <div className="h-3 bg-muted rounded w-1/3" />
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-16 bg-card border border-border rounded-lg">
            <GlossyIcon name="heart" size={48} className="mx-auto opacity-40 mb-4" />
            <p className="text-muted-foreground font-body mb-4">
              Your wishlist is empty
            </p>
            <Link to="/shop">
              <Button>Browse Products</Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {products.map((product) => {
              const price = getEffectivePrice(product);
              return (
                <div
                  key={product.id}
                  className="group bg-card rounded overflow-hidden shadow-sm border border-border"
                >
                  <Link
                    to={`/product/${product.slug}`}
                    className="block relative aspect-square overflow-hidden bg-muted"
                  >
                    <img
                      src={getFirstImage(product.images)}
                      alt={product.name}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </Link>
                  <div className="p-4">
                    <p className="text-xs text-muted-foreground font-body tracking-wide mb-1">
                      {product.categories
                        ?.split(",")[0]
                        ?.split(">")
                        .pop()
                        ?.trim() || "Uncategorized"}
                    </p>
                    <Link to={`/product/${product.slug}`}>
                      <h3 className="font-display text-sm font-semibold mb-2 leading-snug hover:text-primary transition-colors">
                        {product.name}
                      </h3>
                    </Link>
                    <p className="font-body font-bold text-sm text-primary mb-3">
                      {price ? `£${price.toFixed(2)}` : "—"}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        className="flex-1 gap-1.5 text-xs"
                        onClick={() => {
                          addItem(product);
                          toast.success(`${product.name} added to cart`);
                        }}
                      >
                        <GlossyIcon name="cart" size={16} />
                        Add to Cart
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        aria-label={`Remove ${product.name} from wishlist`}
                        title="Remove from wishlist"
                        onClick={() => toggleWishlist(product.id)}
                        className="text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default WishlistPage;
